<script>
  import { createEventDispatcher, onMount, onDestroy, tick } from 'svelte';
  import { getEgresoDets, adjustPickedBySku, adjustPicked, addOrUpdateEgresoDetLine } from '../../models/egresoDetModel';
  import { updateEgresoCab } from '../../models/egresoCabModel';
  import ConfirmDialog from '../Modals/ConfirmDialog.svelte';
  import ProductAutocomplete from '../Pickers/ProductAutocomplete.svelte';
  import { getProductoBySkuExact } from '../../lib/erpProductApi.js';
  import { getStatusPaneTheme } from '../../config.js';
  import { portal } from '../../lib/portal.js';
  import { paneSlide } from '../../lib/paneTransition.js';

  const dispatch = createEventDispatcher();

  export let selectedItem;
  export let closePane;
  export let status;

  let qty = 1;
  let pickMode = 'add';
  let skuInput = '';
  let addSku = '';
  let addDescrip = '';
  let addLineQty = 1;
  let selectedProduct = null;
  let isAddingLine = false;
  let addLineError = '';
  let addLineSuccess = '';
  let scanError = '';
  let scanSuccess = '';
  let isScanning = false;
  let isMarkingReady = false;
  let adjustingLineId = null;
  let showConfirm = false;
  let skuInputEl;
  let refreshInterval;
  let observ = '';
  let observSaved = '';
  let lastObservCabId = null;
  let isSavingObserv = false;
  let observError = '';
  let observSuccess = '';
  let showExtras = false;
  let showOnlyPending = false;
  let lastHitSku = '';

  $: paneStatus = selectedItem?.sta || status;
  $: paneTheme = getStatusPaneTheme(paneStatus);
  $: paneStyle = `--pane-bg: ${paneTheme.bg}; --pane-border: ${paneTheme.border}; --pane-text: ${paneTheme.text};`;
  $: lines = selectedItem?.egresoDet || [];
  $: totalCantidad = lines.reduce((sum, line) => sum + (Number(line.cantidad) || 0), 0);
  $: totalPicked = lines.reduce((sum, line) => sum + (Number(line.picked) || 0), 0);
  $: mismatchedLines = lines.filter(
    (line) => (Number(line.cantidad) || 0) !== (Number(line.picked) || 0)
  );
  $: progressPct = totalCantidad > 0 ? Math.min(100, Math.round((totalPicked / totalCantidad) * 100)) : 0;
  $: pendingCount = mismatchedLines.length;
  $: visibleLines = showOnlyPending ? lines.filter((line) => isLineMismatch(line)) : lines;

  function lineQty(line) {
    return Number(line.cantidad) || 0;
  }

  function linePicked(line) {
    return Number(line.picked) || 0;
  }

  function isLineMismatch(line) {
    return lineQty(line) !== linePicked(line);
  }

  function mismatchLabel(line) {
    const diff = linePicked(line) - lineQty(line);
    if (diff > 0) return `+${diff}`;
    if (diff < 0) return `−${Math.abs(diff)}`;
    return '';
  }

  function isLineComplete(line) {
    const q = lineQty(line);
    return q > 0 && linePicked(line) === q;
  }

  function lineProgress(line) {
    const q = lineQty(line);
    if (q <= 0) return 0;
    return Math.min(100, Math.round((linePicked(line) / q) * 100));
  }

  function focusSkuInputNow() {
    skuInputEl?.focus();
    skuInputEl?.select?.();
  }

  $: if (selectedItem?.id && selectedItem.id !== lastObservCabId) {
    lastObservCabId = selectedItem.id;
    observ = selectedItem.observ || '';
    observSaved = observ;
    observError = '';
    observSuccess = '';
  }

  $: observDirty = observ.trim() !== (observSaved ?? '').trim();

  $: confirmDiscrepancies = mismatchedLines.map((line) => ({
    sku: line.sku,
    descrip: line.descrip,
    requested: lineQty(line),
    picked: linePicked(line),
    note: mismatchLabel(line),
  }));

  async function refreshLines() {
    if (!selectedItem?.id) return;
    const result = await getEgresoDets(selectedItem.id, 1, 200);
    if (result?.items) {
      selectedItem = { ...selectedItem, egresoDet: result.items };
    }
  }

  async function focusSkuInput() {
    await tick();
    focusSkuInputNow();
  }

  onMount(async () => {
    await refreshLines();
    await focusSkuInput();
    refreshInterval = setInterval(refreshLines, 3000);
  });

  onDestroy(() => {
    if (refreshInterval) clearInterval(refreshInterval);
  });

  async function handleProductSelect(event) {
    const product = event.detail;
    selectedProduct = product;
    addSku = product.sku || '';
    addDescrip = product.descrip || '';
    addLineError = '';
  }

  async function handleAddLine() {
    if (!selectedItem?.id || isAddingLine) return;

    const sku = addSku.trim();
    if (!sku) {
      addLineError = 'Selecciona o escribe un SKU';
      return;
    }

    isAddingLine = true;
    addLineError = '';
    addLineSuccess = '';

    try {
      let product = selectedProduct;
      if (!product || product.sku?.toLowerCase() !== sku.toLowerCase()) {
        product = await getProductoBySkuExact(sku);
      }
      if (!product) {
        addLineError = 'Producto no encontrado en ERP';
        return;
      }

      await addOrUpdateEgresoDetLine(selectedItem.id, {
        sku: product.sku,
        descrip: addDescrip.trim() || product.descrip,
        cantidad: addLineQty,
        precio: product.precio,
      });
      addLineSuccess = `Línea agregada: ${sku} × ${Math.max(1, Number(addLineQty) || 1)}`;
      addSku = '';
      addDescrip = '';
      addLineQty = 1;
      selectedProduct = null;
      await refreshLines();
      await focusSkuInput();
    } catch (error) {
      addLineError = error.message || 'No se pudo agregar la línea';
    } finally {
      isAddingLine = false;
    }
  }

  async function handleScanSubmit(event) {
    event?.preventDefault();
    if (isScanning || !selectedItem?.id) return;

    const amount = Math.max(1, Number(qty) || 1);
    const delta = pickMode === 'remove' ? -amount : amount;

    scanError = '';
    scanSuccess = '';
    isScanning = true;

    try {
      const updated = await adjustPickedBySku(selectedItem.id, skuInput, delta);
      lastHitSku = (updated.sku || skuInput || '').toUpperCase();
      scanSuccess = `${delta > 0 ? '+' : '−'}${amount} · ${updated.sku || skuInput}`;
      skuInput = '';
      await refreshLines();
      await focusSkuInput();
    } catch (error) {
      scanError = error.message || (pickMode === 'remove' ? 'Error al restar pick' : 'Error al registrar pick');
    } finally {
      isScanning = false;
    }
  }

  async function handleIncrease(line) {
    if (adjustingLineId) return;
    await applyLineDelta(line, Math.max(1, Number(qty) || 1));
  }

  async function handleDecrease(line) {
    if (adjustingLineId || linePicked(line) <= 0) return;
    await applyLineDelta(line, -Math.max(1, Number(qty) || 1));
  }

  async function applyLineDelta(line, delta) {
    adjustingLineId = line.id;
    scanError = '';
    scanSuccess = '';

    try {
      const updated = await adjustPicked(line.id, delta);
      const amount = Math.abs(delta);
      lastHitSku = (updated.sku || line.sku || '').toUpperCase();
      scanSuccess = `${delta > 0 ? '+' : '−'}${amount} · ${updated.sku || line.sku || '—'}`;
      await refreshLines();
      await focusSkuInput();
    } catch (error) {
      scanError = error.message || 'Error al actualizar pick';
    } finally {
      adjustingLineId = null;
    }
  }

  async function saveObserv() {
    if (!selectedItem?.id || isSavingObserv || !observDirty) return;

    isSavingObserv = true;
    observError = '';
    observSuccess = '';

    const trimmed = observ.trim();

    try {
      await updateEgresoCab(selectedItem.id, { observ: trimmed });
      observSaved = trimmed;
      observ = trimmed;
      selectedItem = { ...selectedItem, observ: trimmed };
      observSuccess = 'Observaciones guardadas';
      dispatch('observUpdated', { id: selectedItem.id, observ: trimmed });
    } catch (error) {
      observError = error.message || 'No se pudieron guardar las observaciones';
    } finally {
      isSavingObserv = false;
    }
  }

  async function requestMarkReady() {
    await refreshLines();
    showConfirm = true;
  }

  async function confirmMarkReady() {
    showConfirm = false;
    if (!selectedItem?.id || isMarkingReady) return;

    isMarkingReady = true;
    try {
      await updateEgresoCab(selectedItem.id, { sta: 'LISTO_PARA_FACTURAR' });
      dispatch('statusChanged', { id: selectedItem.id, sta: 'LISTO_PARA_FACTURAR' });
      closePane();
    } catch (error) {
      scanError = error.message || 'No se pudo actualizar el estado';
    } finally {
      isMarkingReady = false;
    }
  }
</script>

{#if selectedItem}
  <div use:portal class="picker-pane-root">
    <div in:paneSlide out:paneSlide class="picker-pane" style={paneStyle}>
      <header class="picker-pane__header">
        <div class="picker-pane__title-row">
          <div class="min-w-0">
            <h2 class="picker-pane__title">
              Pedido #{selectedItem.numero}
              <span class="picker-pane__client" title={selectedItem.nombre}>{selectedItem.nombre}</span>
            </h2>
            <p class="picker-pane__meta">
              <span>{totalPicked} / {totalCantidad} u.</span>
              <span aria-hidden="true">·</span>
              <span>{pendingCount ? `${pendingCount} pendientes` : 'Completo'}</span>
              <span aria-hidden="true">·</span>
              <span>{progressPct}%</span>
            </p>
          </div>
          <div class="picker-pane__header-actions">
            <button
              type="button"
              class="picker-pane__ghost"
              class:active={showExtras}
              on:click={() => { showExtras = !showExtras; }}
            >
              {showExtras ? 'Ocultar extras' : 'Notas / + producto'}
            </button>
            <button type="button" class="picker-pane__close" on:click={closePane} aria-label="Cerrar">
              ✕
            </button>
          </div>
        </div>
        <div class="picker-pane__progress" aria-hidden="true">
          <div class="picker-pane__progress-fill" style={`width:${progressPct}%`}></div>
        </div>
      </header>

      {#if showExtras}
        <div class="picker-pane__extras">
          <div class="picker-pane__card">
            <label for="picker-observ" class="picker-pane__label">Observaciones</label>
            <textarea
              id="picker-observ"
              bind:value={observ}
              rows="2"
              placeholder="Notas del pedido…"
              disabled={isSavingObserv}
              on:blur={saveObserv}
              class="picker-pane__textarea"
            ></textarea>
            <div class="picker-pane__card-actions">
              <div class="picker-pane__hint">
                {#if observError}
                  <span class="err">{observError}</span>
                {:else if observSuccess}
                  <span class="ok">{observSuccess}</span>
                {:else if observDirty}
                  <span>Cambios sin guardar</span>
                {/if}
              </div>
              <button
                type="button"
                class="picker-pane__mini"
                disabled={isSavingObserv || !observDirty}
                on:click={saveObserv}
              >
                {isSavingObserv ? 'Guardando…' : 'Guardar'}
              </button>
            </div>
          </div>

          <div class="picker-pane__card">
            <p class="picker-pane__label">Agregar producto</p>
            <div class="picker-pane__add-grid">
              <ProductAutocomplete
                mode="sku"
                inputId="picker-add-sku"
                label="SKU"
                placeholder="Buscar SKU…"
                bind:value={addSku}
                disabled={isAddingLine}
                on:select={handleProductSelect}
              />
              <ProductAutocomplete
                mode="descrip"
                inputId="picker-add-descrip"
                label="Descripción"
                placeholder="Buscar descripción…"
                bind:value={addDescrip}
                disabled={isAddingLine}
                on:select={handleProductSelect}
              />
              <div>
                <label for="picker-add-qty" class="picker-pane__label">Cant.</label>
                <input
                  id="picker-add-qty"
                  type="number"
                  min="1"
                  step="1"
                  bind:value={addLineQty}
                  disabled={isAddingLine}
                  class="picker-pane__input"
                />
              </div>
              <button
                type="button"
                class="picker-pane__mini picker-pane__mini--wide"
                disabled={isAddingLine || !addSku.trim()}
                on:click={handleAddLine}
              >
                {isAddingLine ? 'Agregando…' : 'Agregar línea'}
              </button>
            </div>
            {#if addLineError}
              <p class="picker-pane__msg err">{addLineError}</p>
            {/if}
            {#if addLineSuccess}
              <p class="picker-pane__msg ok">{addLineSuccess}</p>
            {/if}
          </div>
        </div>
      {/if}

      <section class="picker-pane__scan" class:remove={pickMode === 'remove'}>
        <form class="picker-pane__scan-form" on:submit={handleScanSubmit}>
          <div class="picker-pane__mode" role="group" aria-label="Modo de pick">
            <button
              type="button"
              class="picker-pane__mode-btn"
              class:on={pickMode === 'add'}
              on:click={() => { pickMode = 'add'; focusSkuInputNow(); }}
            >
              + Agregar
            </button>
            <button
              type="button"
              class="picker-pane__mode-btn remove"
              class:on={pickMode === 'remove'}
              on:click={() => { pickMode = 'remove'; focusSkuInputNow(); }}
            >
              − Restar
            </button>
          </div>

          <div>
            <label for="picker-qty" class="picker-pane__label">Cant.</label>
            <div class="picker-pane__qty">
              <button type="button" aria-label="Menos" on:click={() => { qty = Math.max(1, (Number(qty) || 1) - 1); }}>−</button>
              <input
                id="picker-qty"
                type="number"
                min="1"
                step="1"
                inputmode="numeric"
                bind:value={qty}
              />
              <button type="button" aria-label="Más" on:click={() => { qty = (Number(qty) || 1) + 1; }}>+</button>
            </div>
          </div>

          <div class="picker-pane__sku-wrap">
            <label for="picker-sku" class="picker-pane__label">Escanear SKU / código</label>
            <input
              id="picker-sku"
              type="text"
              bind:this={skuInputEl}
              bind:value={skuInput}
              placeholder="Apunta el lector aquí y Enter…"
              autocomplete="off"
              enterkeyhint="done"
              class="picker-pane__sku"
            />
          </div>

          <button
            type="submit"
            class="picker-pane__scan-go"
            class:remove={pickMode === 'remove'}
            disabled={isScanning || !skuInput.trim()}
          >
            {#if isScanning}
              {pickMode === 'remove' ? 'Restando…' : 'Registrando…'}
            {:else}
              {pickMode === 'remove' ? 'Restar' : 'Registrar'}
            {/if}
          </button>
        </form>
        <div class="picker-pane__scan-feedback" aria-live="polite">
          {#if scanError}
            <span class="err">{scanError}</span>
          {:else if scanSuccess}
            <span class="ok">{scanSuccess}</span>
          {:else}
            <span class="muted">Enter confirma · el foco vuelve al SKU automáticamente</span>
          {/if}
        </div>
      </section>

      <div class="picker-pane__body">
        <div class="picker-pane__table-toolbar">
          <h3 class="picker-pane__table-title">
            Detalle de productos
            <span>({visibleLines.length}{showOnlyPending ? ` de ${lines.length}` : ''})</span>
          </h3>
          <label class="picker-pane__filter">
            <input type="checkbox" bind:checked={showOnlyPending} />
            Solo pendientes
          </label>
        </div>

        <div class="picker-pane__table-wrap">
          <table class="picker-pane__table">
            <thead>
              <tr>
                <th>SKU</th>
                <th>Descripción</th>
                <th class="num">Pedida</th>
                <th class="num">Pick</th>
                <th class="actions">Ajuste</th>
              </tr>
            </thead>
            <tbody>
              {#each visibleLines as line (line.id)}
                <tr
                  class:mismatch={isLineMismatch(line)}
                  class:complete={isLineComplete(line)}
                  class:hit={(line.sku || '').toUpperCase() === lastHitSku}
                >
                  <td class="sku">{line.sku || '—'}</td>
                  <td class="descrip">
                    <div class="descrip-text">{line.descrip || '—'}</div>
                    <div class="line-bar" aria-hidden="true">
                      <div class="line-bar__fill" style={`width:${lineProgress(line)}%`}></div>
                    </div>
                  </td>
                  <td class="num">{line.cantidad ?? 0}</td>
                  <td class="num">
                    <span class="pick-val">{line.picked ?? 0}</span>
                    {#if isLineMismatch(line)}
                      <span class="diff">{mismatchLabel(line)}</span>
                    {/if}
                  </td>
                  <td class="actions">
                    <div class="stepper">
                      <button
                        type="button"
                        title={`Restar ${Math.max(1, Number(qty) || 1)}`}
                        disabled={linePicked(line) <= 0 || adjustingLineId === line.id}
                        on:click={() => handleDecrease(line)}
                      >−</button>
                      <button
                        type="button"
                        class="plus"
                        title={`Agregar ${Math.max(1, Number(qty) || 1)}`}
                        disabled={adjustingLineId === line.id}
                        on:click={() => handleIncrease(line)}
                      >+</button>
                    </div>
                  </td>
                </tr>
              {:else}
                <tr>
                  <td colspan="5" class="empty">
                    {showOnlyPending ? 'No hay líneas pendientes' : 'Sin líneas de detalle'}
                  </td>
                </tr>
              {/each}
            </tbody>
          </table>
        </div>
      </div>

      <footer class="picker-pane__footer">
        <button
          type="button"
          class="picker-pane__ready"
          disabled={isMarkingReady}
          on:click={requestMarkReady}
        >
          {isMarkingReady ? 'Actualizando…' : 'Listo para facturar'}
        </button>
      </footer>
    </div>
  </div>
{/if}

<ConfirmDialog
  show={showConfirm}
  variant={mismatchedLines.length ? 'warning' : 'default'}
  title="Listo para facturar"
  message={mismatchedLines.length
    ? 'Hay diferencias entre lo solicitado y lo pickeado. Revisa antes de continuar.'
    : 'El pedido pasará a listo para facturar y dejará de aparecer en tu lista.'}
  confirmLabel="Confirmar y enviar"
  cancelLabel="Volver"
  discrepancies={confirmDiscrepancies}
  on:confirm={confirmMarkReady}
  on:cancel={() => { showConfirm = false; }}
/>

<style>
  .picker-pane-root {
    position: fixed;
    inset: 0;
    z-index: 9999;
    pointer-events: none;
  }

  .picker-pane {
    pointer-events: auto;
    position: fixed;
    inset: 0;
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    background-color: var(--pane-bg);
    color: var(--pane-text);
    opacity: 1;
    will-change: transform;
  }

  .picker-pane__header,
  .picker-pane__extras,
  .picker-pane__scan,
  .picker-pane__footer {
    flex-shrink: 0;
  }

  .picker-pane__header {
    padding: 0.7rem 1rem 0.55rem;
    border-bottom: 1px solid rgba(0, 0, 0, 0.1);
    background: rgba(255, 255, 255, 0.35);
  }

  .picker-pane__title-row {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
  }

  .picker-pane__title {
    margin: 0;
    font-size: 1.15rem;
    font-weight: 800;
    line-height: 1.25;
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 0.5rem;
  }

  .picker-pane__client {
    font-size: 0.95rem;
    font-weight: 600;
    opacity: 0.9;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: min(55vw, 32rem);
  }

  .picker-pane__meta {
    margin: 0.25rem 0 0;
    font-size: 0.8rem;
    opacity: 0.85;
    display: flex;
    flex-wrap: wrap;
    gap: 0.35rem;
  }

  .picker-pane__header-actions {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex-shrink: 0;
  }

  .picker-pane__ghost {
    padding: 0.4rem 0.75rem;
    border-radius: 0.5rem;
    border: 1px solid rgba(0, 0, 0, 0.15);
    background: rgba(255, 255, 255, 0.75);
    font-size: 0.75rem;
    font-weight: 700;
    color: inherit;
  }

  .picker-pane__ghost.active {
    background: #1e293b;
    color: #fff;
    border-color: #1e293b;
  }

  .picker-pane__close {
    width: 2.25rem;
    height: 2.25rem;
    border-radius: 0.5rem;
    font-size: 1.1rem;
    line-height: 1;
    color: inherit;
    opacity: 0.7;
  }

  .picker-pane__close:hover {
    opacity: 1;
    color: #dc2626;
    background: rgba(255, 255, 255, 0.5);
  }

  .picker-pane__progress {
    margin-top: 0.55rem;
    height: 0.45rem;
    border-radius: 999px;
    background: rgba(0, 0, 0, 0.12);
    overflow: hidden;
  }

  .picker-pane__progress-fill {
    height: 100%;
    border-radius: inherit;
    background: linear-gradient(90deg, #f97316, #14b8a6);
    transition: width 0.25s ease;
  }

  .picker-pane__extras {
    display: grid;
    grid-template-columns: 1fr 1.35fr;
    gap: 0.75rem;
    padding: 0.75rem 1rem;
    border-bottom: 1px solid rgba(0, 0, 0, 0.08);
    background: rgba(255, 255, 255, 0.22);
  }

  @media (max-width: 900px) {
    .picker-pane__extras {
      grid-template-columns: 1fr;
    }
  }

  .picker-pane__card {
    background: #fff;
    border-radius: 0.65rem;
    padding: 0.75rem;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
  }

  .picker-pane__label {
    display: block;
    font-size: 0.7rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #334155;
    margin-bottom: 0.3rem;
  }

  .picker-pane__textarea,
  .picker-pane__input {
    width: 100%;
    padding: 0.5rem 0.65rem;
    border: 1px solid #cbd5e1;
    border-radius: 0.45rem;
    font-size: 0.875rem;
    color: #1e293b;
  }

  .picker-pane__textarea {
    resize: vertical;
    min-height: 3rem;
  }

  .picker-pane__card-actions {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
    margin-top: 0.4rem;
  }

  .picker-pane__hint {
    min-height: 1.1rem;
    font-size: 0.75rem;
    color: #64748b;
  }

  .picker-pane__add-grid {
    display: grid;
    grid-template-columns: 1fr 1fr 5.5rem auto;
    gap: 0.55rem;
    align-items: end;
  }

  @media (max-width: 1100px) {
    .picker-pane__add-grid {
      grid-template-columns: 1fr 1fr;
    }
  }

  .picker-pane__mini {
    padding: 0.5rem 0.85rem;
    border-radius: 0.45rem;
    background: #1e293b;
    color: #fff;
    font-size: 0.8rem;
    font-weight: 700;
  }

  .picker-pane__mini:disabled {
    opacity: 0.4;
  }

  .picker-pane__mini--wide {
    align-self: end;
    height: 2.4rem;
  }

  .picker-pane__msg {
    margin-top: 0.4rem;
    font-size: 0.8rem;
  }

  .picker-pane__scan {
    padding: 0.75rem 1rem;
    border-bottom: 1px solid rgba(0, 0, 0, 0.1);
    background: rgba(255, 255, 255, 0.55);
  }

  .picker-pane__scan.remove {
    background: rgba(254, 226, 226, 0.55);
  }

  .picker-pane__scan-form {
    display: grid;
    grid-template-columns: auto 9rem minmax(0, 1fr) 9rem;
    gap: 0.75rem;
    align-items: end;
  }

  @media (max-width: 900px) {
    .picker-pane__scan-form {
      grid-template-columns: 1fr 1fr;
    }

    .picker-pane__sku-wrap,
    .picker-pane__scan-go {
      grid-column: 1 / -1;
    }
  }

  .picker-pane__mode {
    display: inline-flex;
    border: 1px solid #cbd5e1;
    border-radius: 0.6rem;
    overflow: hidden;
    background: #fff;
  }

  .picker-pane__mode-btn {
    padding: 0.7rem 0.9rem;
    font-size: 0.85rem;
    font-weight: 700;
    color: #475569;
    background: #fff;
  }

  .picker-pane__mode-btn.on {
    background: #f97316;
    color: #fff;
  }

  .picker-pane__mode-btn.remove.on {
    background: #ef4444;
  }

  .picker-pane__qty {
    display: grid;
    grid-template-columns: 2.25rem minmax(2.75rem, 1fr) 2.25rem;
    width: 100%;
    min-width: 8.5rem;
    border: 1px solid #cbd5e1;
    border-radius: 0.6rem;
    overflow: hidden;
    background: #fff;
    height: 3rem;
  }

  .picker-pane__qty button {
    font-size: 1.1rem;
    font-weight: 700;
    background: #f8fafc;
    color: #334155;
  }

  .picker-pane__qty input {
    width: 100%;
    min-width: 2.75rem;
    border: none;
    text-align: center;
    font-size: 1.25rem;
    font-weight: 800;
    color: #0f172a;
    appearance: textfield;
    -moz-appearance: textfield;
    padding: 0 0.15rem;
  }

  .picker-pane__qty input::-webkit-outer-spin-button,
  .picker-pane__qty input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  .picker-pane__sku {
    width: 100%;
    height: 3rem;
    padding: 0 1rem;
    border: 2px solid #fb923c;
    border-radius: 0.65rem;
    font-size: 1.2rem;
    font-weight: 650;
    letter-spacing: 0.02em;
    color: #0f172a;
    background: #fff;
    box-shadow: 0 0 0 3px rgba(249, 115, 22, 0.12);
  }

  .picker-pane__sku:focus {
    outline: none;
    border-color: #ea580c;
    box-shadow: 0 0 0 4px rgba(249, 115, 22, 0.22);
  }

  .picker-pane__scan.remove .picker-pane__sku {
    border-color: #f87171;
    box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.12);
  }

  .picker-pane__scan-go {
    height: 3rem;
    border-radius: 0.65rem;
    background: #f97316;
    color: #fff;
    font-size: 0.95rem;
    font-weight: 800;
  }

  .picker-pane__scan-go.remove {
    background: #ef4444;
  }

  .picker-pane__scan-go:disabled {
    opacity: 0.45;
  }

  .picker-pane__scan-feedback {
    margin-top: 0.45rem;
    min-height: 1.2rem;
    font-size: 0.85rem;
    font-weight: 600;
  }

  .ok { color: #0f766e; }
  .err { color: #dc2626; }
  .muted { color: #64748b; font-weight: 500; }

  .picker-pane__body {
    flex: 1 1 auto;
    min-height: 50vh;
    display: flex;
    flex-direction: column;
    padding: 0.65rem 1rem 0.75rem;
    overflow: hidden;
  }

  .picker-pane__table-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    margin-bottom: 0.45rem;
    flex-shrink: 0;
  }

  .picker-pane__table-title {
    margin: 0;
    font-size: 0.8rem;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  .picker-pane__table-title span {
    font-weight: 600;
    opacity: 0.7;
    text-transform: none;
    letter-spacing: 0;
  }

  .picker-pane__filter {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    font-size: 0.8rem;
    font-weight: 600;
    user-select: none;
  }

  .picker-pane__table-wrap {
    flex: 1;
    min-height: 0;
    overflow: auto;
    background: #fff;
    border-radius: 0.75rem;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  }

  .picker-pane__table {
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;
    font-size: 0.9rem;
  }

  .picker-pane__table thead th {
    position: sticky;
    top: 0;
    z-index: 1;
    background: #f8fafc;
    color: #475569;
    font-size: 0.72rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    text-align: left;
    padding: 0.7rem 0.85rem;
    border-bottom: 1px solid #e2e8f0;
  }

  .picker-pane__table th.num,
  .picker-pane__table td.num {
    text-align: right;
  }

  .picker-pane__table th.actions,
  .picker-pane__table td.actions {
    text-align: right;
    width: 7.5rem;
  }

  .picker-pane__table tbody td {
    padding: 0.7rem 0.85rem;
    border-bottom: 1px solid #f1f5f9;
    vertical-align: middle;
    color: #0f172a;
  }

  .picker-pane__table tr.mismatch {
    background: #fffbeb;
  }

  .picker-pane__table tr.complete {
    background: #f0fdf4;
  }

  .picker-pane__table tr.hit {
    outline: 2px solid #fb923c;
    outline-offset: -2px;
  }

  .sku {
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
    font-size: 0.82rem;
    font-weight: 700;
    white-space: nowrap;
  }

  .descrip {
    min-width: 0;
  }

  .descrip {
    min-width: 0;
  }

  .descrip-text {
    line-height: 1.3;
  }

  .line-bar {
    margin-top: 0.35rem;
    height: 0.28rem;
    border-radius: 999px;
    background: #e2e8f0;
    overflow: hidden;
    max-width: 16rem;
  }

  .line-bar__fill {
    height: 100%;
    background: #14b8a6;
    transition: width 0.2s ease;
  }

  tr.mismatch .line-bar__fill {
    background: #f59e0b;
  }

  .pick-val {
    font-weight: 800;
    font-size: 1rem;
  }

  .diff {
    display: inline-block;
    margin-left: 0.35rem;
    font-size: 0.75rem;
    font-weight: 700;
    color: #b45309;
  }

  .stepper {
    display: inline-flex;
    gap: 0.35rem;
  }

  .stepper button {
    width: 2.15rem;
    height: 2.15rem;
    border-radius: 0.45rem;
    border: 1px solid #fecaca;
    background: #fef2f2;
    color: #b91c1c;
    font-size: 1.05rem;
    font-weight: 800;
  }

  .stepper button.plus {
    border-color: #fed7aa;
    background: #fff7ed;
    color: #c2410c;
  }

  .stepper button:disabled {
    opacity: 0.35;
  }

  .empty {
    text-align: center;
    padding: 2.5rem 1rem !important;
    color: #64748b;
  }

  .picker-pane__footer {
    padding: 0.75rem 1rem;
    border-top: 1px solid rgba(0, 0, 0, 0.1);
    background: rgba(255, 255, 255, 0.45);
  }

  .picker-pane__ready {
    width: 100%;
    padding: 0.85rem 1rem;
    border-radius: 0.65rem;
    background: #14b8a6;
    color: #fff;
    font-size: 0.95rem;
    font-weight: 800;
  }

  .picker-pane__ready:disabled {
    opacity: 0.45;
  }
</style>
